import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { apiErrorMessage, useGet } from "../../api";
import { AuditChangeList } from "../../components/workbench/entity-audit-trail";
import { IKindredDomainAuditResponse } from "../../interfaces";

const PAGE_SIZE = 25;

/**
 * Every column change on the domain's coupons, deals, offers and brands, from
 * the Kindred SQL audit — so PowerApp, this app and domain-review runs all show.
 */
export const DomainChanges: React.FC<{
  domain: string;
  /** Empty means the server's default: the last 90 days. */
  dateFrom: string;
  dateTo: string;
  refreshKey: number;
}> = ({ domain, dateFrom, dateTo, refreshKey }) => {
  const [page, setPage] = useState(1);
  const [seen, setSeen] = useState({ domain, dateFrom, dateTo });
  if (
    seen.domain !== domain ||
    seen.dateFrom !== dateFrom ||
    seen.dateTo !== dateTo
  ) {
    setSeen({ domain, dateFrom, dateTo });
    setPage(1);
  }

  const p = new URLSearchParams({
    domain,
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (dateFrom) p.set("dateFrom", new Date(dateFrom).toISOString());
  if (dateTo) {
    const end = new Date(dateTo);
    end.setHours(23, 59, 59, 999);
    p.set("dateTo", end.toISOString());
  }
  const { data, loading, error, refetch } = useGet<IKindredDomainAuditResponse>(
    `work-pipeline/domain-changes?${p.toString()}`,
    true
  );
  // The route rejects unknown params, so Refresh refetches rather than cache-busting the URL.
  const lastKey = useRef(refreshKey);
  useEffect(() => {
    if (lastKey.current === refreshKey) return;
    lastKey.current = refreshKey;
    refetch().catch(() => null);
  }, [refreshKey, refetch]);
  const pag = data?.pagination;

  return (
    <div className="p-3">
      {error && (
        <Alert variant="danger" className="small">
          Could not load the changes: {apiErrorMessage(error)}
        </Alert>
      )}
      {loading && !data ? (
        <div className="text-center p-4">
          <Spinner animation="border" size="sm" /> Loading…
        </div>
      ) : data ? (
        <>
          <p className="small text-muted mb-2">
            {pag?.total ?? 0} change{pag?.total === 1 ? "" : "s"} across{" "}
            {data.entityCount} coupons, deals, offers and brands on{" "}
            <strong>{data.domain}</strong>, from every app (PowerApp, this app
            and domain review),{" "}
            {dateFrom ? "in the chosen dates" : "in the last 90 days"}. Set From
            / To to look further back.
          </p>
          {data.entityCount === 0 ? (
            <div className="text-muted small">
              Nothing in Kindred is on {data.domain}. Field changes need the
              exact domain, e.g. lowes.com.
            </div>
          ) : !data.changes.length ? (
            <div className="text-muted small">No changes in this period.</div>
          ) : (
            <AuditChangeList
              key={page}
              changes={data.changes}
              defaultOpenFirst
            />
          )}
          {pag && pag.totalPages > 1 && (
            <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((n) => Math.max(1, n - 1))}
                title="Newer changes"
              >
                Prev
              </Button>
              <small className="text-muted">
                Page {pag.page} of {pag.totalPages}
              </small>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page >= pag.totalPages}
                onClick={() => setPage((n) => n + 1)}
                title="Older changes"
              >
                Next
              </Button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
